import { formatDate } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { Observable } from 'rxjs';
import { AuthService } from 'src/app/shared/authentication/auth.service';
import { deepCopy, getFilesFromInputTarget } from 'src/app/shared/model/utility';

interface ImportExportResponse {
  totalImported?: number;
}

@Component({
  selector: 'app-import-export-widget-base',
  template: ``,
  standalone: false
})
export class ImportExportWidgetBaseComponent
  <TResponse extends ImportExportResponse, TEntity extends any> {

  @Input() selectedFolder: string;
  @Input() showExportOptions = true;
  @Input() selectedFolderStatus = false;
  @Input() selectedForExport: TEntity[];

  @Output() clearSelected: EventEmitter<void> = new EventEmitter();
  @Output() exportClicked: EventEmitter<boolean> = new EventEmitter();
  @Output() selectAllFromFolder: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('importDialog') importDialog: TemplateRef<any>;
  @ViewChild('exportDialog') exportDialog: TemplateRef<any>;

  exportInProgress = false;
  importErrors: string[];
  localizedExportError: string;
  localizedExportMessage: string;
  
  importLoading = false;
  readyForImport = false;
  importComplete = false;
  selectedImportFile: File;
  importResponse: TResponse;
  localizedImportMessage: string;

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    protected translation: L10nTranslationService,
    protected router: Router,
    protected dialog: MatDialog,
    private authService: AuthService
  ) { }

  onClearSelected() {
    this.clearSelected.emit();
  }

  onExportClick() {
    this.exportInProgress = !this.exportInProgress;
    this.exportClicked.emit(this.exportInProgress);
  }

  onExecuteExportRequest(request$: Observable<Blob>, fileSuffix: string, dialogTitle: string, resultMessagePath: string) {
    this.localizedExportMessage = this.translation.translate(dialogTitle, { count: this.selectedForExport.length });
    const dialogRef = this.onLaunchDialog(this.exportDialog);
    dialogRef.afterClosed().subscribe(_ => this.resetExportStatus());

    request$.subscribe({
      next: file => {
        if (file.size < 1) {
          this.localizedExportError = this.translation.translate('The exported file contained no data.');
          this.localizedExportMessage = this.translation.translate('No Templates were Exported');
          return;
        }

        this.localizedExportMessage = this.translation.translate(resultMessagePath, { count: this.selectedForExport.length });
        const url = URL.createObjectURL(file);
        const anchor = document.createElement('a');
        anchor.href = url;
        const regionName = this.authService.user.snapshot.agency.replace(/\W/g, '_')
        const fileName = regionName + fileSuffix + ".json";
        anchor.download = formatDate(new Date(), 'yyyy-MM-dd', 'en') + fileName;

        // Attach an invisible anchor to the DOM and click it to download the file
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);

        this.exportInProgress = false;
      },
      error: err => {
        this.localizedExportMessage = this.translation.translate('No Templates were Exported');
        this.localizedExportError = err.message;
      }
    })
  }

  resetExportStatus() {
    this.exportInProgress = false;
    this.localizedExportError = null;
    this.localizedExportMessage = null;
    this.exportClicked.emit(this.exportInProgress);
    this.onClearSelected();
  }

  onSelectFileToImport(target: EventTarget) {
    this.readyForImport = false;
    this.importComplete = false;
    const files = getFilesFromInputTarget(target);
    if (files) {
      this.selectedImportFile = files[0];
    }
  }

  onImportClick(refreshRoute: string) {
    this.exportInProgress = false;
    this.exportClicked.emit(this.exportInProgress)
    const dialogRef = this.onLaunchDialog(this.importDialog);
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.router.navigate(['']).then(_ => {
          this.router.navigate([refreshRoute], { replaceUrl: true });
        });
      }
      this.resetImportStatus();
    })
  }

  removeSelectedImportFile() {
    // Clear file (allows re-importing same filename)
    this.fileInput.nativeElement.value = '';
    this.selectedImportFile = null;
  }

  onExecuteImportRequest(request$: Observable<TResponse>, resultMessagePath: string) {

    this.importLoading = true;
    this.localizedImportMessage = this.translation.translate('Importing Templates from File');

    request$.subscribe({
      next: res => {
        this.importResponse = res;
        this.importLoading = false;
        this.localizedImportMessage = this.translation.translate(resultMessagePath, { count: this.importResponse.totalImported })
        this.importComplete = true;
      },
      error: err => {
        this.importLoading = false;
        this.importComplete = true;
        if (!err?.error?.ResponseStatus) {
          this.importResponse = deepCopy(err.error);
          this.localizedImportMessage = this.translation.translate(resultMessagePath, { count: err.error.totalImported })
          this.importErrors = err.error.status?.messages?.map(m => m.message);
        }

        if (err?.error?.ResponseStatus) {
          this.localizedImportMessage = null
          this.importErrors = [
            err?.error?.ResponseStatus?.ErrorCode,
            err?.error?.ResponseStatus?.Message
          ];
        }
      }
    })
  }

  resetImportStatus() {
    this.removeSelectedImportFile();
    this.importLoading = false;
    this.readyForImport = false;
    this.importComplete = false;
    this.importResponse = null;
    this.importErrors = null;
  }

  onLaunchDialog(dialog: TemplateRef<any>) {
    return this.dialog.open(dialog, {
      autoFocus: false,
      hasBackdrop: true,
      disableClose: true,
      width: '600px',
      maxWidth: '100%'
    });
  }

  partialAllFolder(pathKey: string) {
    return !this.selectedFolderStatus && this.selectedForExport?.some(x => x[pathKey]?.startsWith(this.selectedFolder));
  }
}
